/**
 * Stotram parser — detects shloka structure, uvāca markers, dhyānam sections.
 * Ported in spirit from jnana-native/src/bmScreens/bhajan/view/lyricsRendererView/stotram/parser.ts.
 *
 * Heuristic: a stotram has at least 2 numbered shlokas, often surrounded by
 * "uvāca" speakers and "dhyānam" / "phala śruti" section headers.
 *
 * Two things this file has to get right, both learned the hard way:
 *
 * NUMBERING. Verses in this catalog are numbered `1.` at the START of a line
 * ("1. vyāsaṁ vaśiṣṭhanaptāraṁ …"); nothing uses the `। १ ।` form the first
 * cut required, so `isStotram` was false for all 44 stotram-named bhajans and
 * the structured renderer had never run, in any language. The rule stays
 * narrow on purpose: native's full pattern set includes "line ends in a digit",
 * which over a 150-bhajan sample matches 82% of the catalog. Native can afford
 * that because numbering is only one of several inputs to its `isMatch`; here
 * it is the whole gate, so it is `N.` / `N)` at line start and nothing looser —
 * 2% of the sample, which is the stotrams and the numbered nāmāvaḷis.
 *
 * LANGUAGE. Lyrics arrive in the reader's own script, so heading words must be
 * matched per-language, from the same sectionWords table the pallavi/caraṇaṁ
 * headings already use. Latin/IAST spellings stay in the alternation always,
 * because transliterated headings appear in real data whatever `?l=` says.
 */
import { finalNasalVariants, sandhiInitialVariants, sectionWordsForLanguage } from './sectionWords';

export interface StotramSection {
  kind: 'header' | 'shloka';
  /** display label for header (e.g. "śiva uvāca", "dhyānam"); shloka number for shlokas */
  label?: string;
  /** number for shloka sections only */
  number?: number;
  /** raw lines of this section */
  lines: string[];
}

export interface ParsedStotram {
  isStotram: boolean;
  sections: StotramSection[];
}

/**
 * A verse number at the START of the line — `1.`, `12)`, `१.` — which is how
 * this catalog numbers shlokas. The trailing-danda form is kept alongside it
 * because transliterated texts do use `। १ ।`.
 */
const SHLOKA_LEADING_NUMBER_RE = /^\s*([0-9०-९]{1,3})\s*[.)]/;
const SHLOKA_TRAILING_NUMBER_RE = /[|‖।]\s*([0-9०-९]{1,3})\s*[|‖।]\s*$/;

/**
 * Latin/IAST spellings, matched in every language — see the file header. These
 * are regex fragments, not literals, so they never go through `escape`.
 *
 * `[mṁṃ]` because IAST writes a final nasal with a dot above: the catalog
 * carries both "dhyānam" and "dhyānaṁ", and sectionWords' own English entries
 * already use caraṇaṁ / ślōkaṁ.
 */
const LATIN_UVACA = ['uvāca', 'uvaca'];
const LATIN_DHYANAM = ['dhyāna[mṁṃ]', 'dhyana[mṁṃ]'];
const LATIN_SAMPURNAM = ['sampūrṇa[mṁṃ]', 'sampurṇa[mṁṃ]', 'sampurna[mṁṃ]'];
const LATIN_PHALA_SRUTI = ['phala\\s*śruti', 'phala\\s*shruti', 'फलश्रुति'];
const LATIN_MANGALAM = ['maṅgaḷa[mṁṃ]', 'maṅgala[mṁṃ]', 'mangala[mṁṃ]'];

function escape(word: string): string {
  return word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** `a|b|c`, longest first so a word that is a prefix of another cannot win. */
function alternation(parts: string[]): string {
  return Array.from(new Set(parts))
    .sort((a, b) => b.length - a.length)
    .join('|');
}

interface HeaderMatchers {
  uvaca: RegExp;
  standalone: RegExp;
}

/**
 * Heading matchers for one language. `uvaca` matches "<speaker> uvāca" in the
 * reader's script; `standalone` matches a heading that is the whole line
 * (dhyānam, sampūrṇam, phala śruti, maṅgaḷam).
 */
function headerMatchers(languageCode: string): HeaderMatchers {
  const w = sectionWordsForLanguage(languageCode);
  // Both spellings of a word-final nasal — the catalog uses whichever the
  // individual text was typed with. See finalNasalVariants.
  const localised = (word: string): string[] => finalNasalVariants(word).map(escape);
  // uvāca also needs its fused form, where sandhi turns the leading vowel into
  // a sign on the previous consonant. See sandhiInitialVariants.
  const uvaca = alternation([
    ...localised(w.uvaca).flatMap((v) => sandhiInitialVariants(v)),
    ...LATIN_UVACA,
  ]);
  const standalone = alternation([
    ...localised(w.dhyanam),
    ...localised(w.sampurnam),
    ...localised(w.phalasruti),
    ...LATIN_DHYANAM,
    ...LATIN_SAMPURNAM,
    ...LATIN_PHALA_SRUTI,
    ...LATIN_MANGALAM,
  ]);
  return {
    // The speaker's name may be joined to the word by sandhi with no space
    // ("śrībhagavānuvāca:", "దేవ్యువాచ"), so the separator is optional and the
    // speaker may be absent entirely. What keeps a verse line out is the `$`
    // anchor: the line has to END at the word, and a verse that merely contains
    // "uvāca" carries on past it. Trailing ":-" and ":" are both in the data.
    uvaca: new RegExp(`^\\s*(.*?)\\s*(?:${uvaca})\\s*[:：]?\\s*-?\\s*$`, 'i'),
    // `(…)` allows an English gloss after the word — "Dhyānam (meditation):".
    // A parenthesis only, never free prose, or a verse opening with a heading
    // word would be taken for a heading.
    standalone: new RegExp(
      `^\\s*(?:${standalone})\\s*(?:\\([^)]*\\))?\\s*[:：।॥]?\\s*-?\\s*$`,
      'i'
    ),
  };
}

function devToInt(s: string): number | null {
  // Devanagari digits ०-९ to 0-9
  const map: Record<string, string> = { '०':'0','१':'1','२':'2','३':'3','४':'4','५':'5','६':'6','७':'7','८':'8','९':'9' };
  const norm = s.replace(/[०-९]/g, (c) => map[c] ?? c);
  const n = parseInt(norm, 10);
  return Number.isFinite(n) ? n : null;
}

function isHeaderLine(line: string, matchers: HeaderMatchers): { ok: boolean; label?: string } {
  const t = line.trim();
  if (!t) return { ok: false };
  // A heading is a short line on its own; the length guard stops a lyric that
  // happens to contain "uvāca" mid-verse from splitting the document.
  if (t.length < 80 && (matchers.uvaca.test(t) || matchers.standalone.test(t))) {
    return { ok: true, label: t.replace(/\s*[:：।॥]\s*-?\s*$/, '').trim() };
  }
  return { ok: false };
}

function shlokaNumber(line: string): number | null {
  const m = line.match(SHLOKA_LEADING_NUMBER_RE) ?? line.match(SHLOKA_TRAILING_NUMBER_RE);
  if (!m) return null;
  return devToInt(m[1]);
}

/**
 * @param languageCode the reader's language ('te', 'kn', …). Headings are
 *   matched in that script as well as in Latin; defaults to English.
 */
export function parseStotram(text: string, languageCode: string = 'en'): ParsedStotram {
  if (!text || !text.trim()) return { isStotram: false, sections: [] };
  const matchers = headerMatchers(languageCode);

  const lines = text.split(/\r?\n/);
  const sections: StotramSection[] = [];
  let buffer: string[] = [];
  let shlokaCount = 0;

  const flushBufferAsShloka = (num?: number) => {
    if (buffer.length === 0) return;
    sections.push({ kind: 'shloka', number: num, lines: [...buffer] });
    buffer = [];
  };

  // A leading number OPENS its verse and a trailing one CLOSES it, so the
  // number for the block being accumulated is only known up front in the
  // leading case — hence carrying it rather than passing it at flush time.
  let pendingNumber: number | undefined;
  const flushPending = () => {
    const n = pendingNumber;
    pendingNumber = undefined;
    flushBufferAsShloka(n);
  };

  for (const raw of lines) {
    const line = raw.trimEnd();

    const header = isHeaderLine(line, matchers);
    if (header.ok) {
      flushPending();
      sections.push({ kind: 'header', label: header.label, lines: [] });
      continue;
    }

    const leading = line.match(SHLOKA_LEADING_NUMBER_RE);
    if (leading) {
      // Close the previous verse before starting this one.
      flushPending();
      pendingNumber = devToInt(leading[1]) ?? undefined;
      shlokaCount++;
      // Drop the number from the text: it becomes `section.number`, which the
      // renderer draws in its own gutter, and leaving it here prints it twice.
      buffer.push(line.slice(leading[0].length).trimStart());
      continue;
    }

    buffer.push(line);

    const num = shlokaNumber(line);
    if (num != null) {
      shlokaCount++;
      pendingNumber = undefined;
      flushBufferAsShloka(num);
    }
  }
  // any tail content
  if (buffer.length > 0) {
    // if last buffer ends without a number but we already saw shlokas, treat as shloka (closing)
    if (shlokaCount > 0) flushPending();
    else sections.push({ kind: 'shloka', lines: [...buffer] });
  }

  // require ≥2 numbered shlokas to consider it a stotram. Otherwise treat as plain bhajan.
  const isStotram = shlokaCount >= 2;
  return { isStotram, sections };
}
